import dayjs from 'dayjs';
import { useCallback, useEffect, useRef } from 'react';

import { SlotActions } from '@Actions/SlotActions';
import { showNotification } from '@Components/design-system/notification/notification';
import { NotificationType } from '@Components/design-system/notification/notification';
import { IQueryData, updateLocationCode } from '@Reducers/common';
import { IRescheduleConsultationData } from '@Reducers/consultation';
import { Selectables } from '@Reducers/selectables';
import { ISlot, ISlotData } from '@Reducers/slot/types';
import { useAppDispatch, useAppSelector } from '@Redux/hooks';
import { CONSULTATION_STATE, INSTANT_LOCATION_CODE, ONLINE_LOCATION_ID, PAGE } from '@Utils/constants';
import useAnasRouter from '@Utils/hooks/anas-router';
import { Routes } from '@Utils/hooks/anas-router/routes';
import useScheduleAppointment from '../../schedule-appointment';
import useHoldSlot from '../../use-hold-slot';

import usePosthog, { CtaEvents } from '@Utils/hooks/posthog';
import useInvoice from '@Utils/hooks/preview-invoice';

import useConsultationType from '../../consultation-type';
import useGetDays from '../../get-days';
import { Direction } from '../constants';
import { Rule } from './type';

export const useSlotRule = (initializeSlotEvents: () => void): Rule => {
  const dispatch = useAppDispatch();
  const router = useAnasRouter();
  const selectables: Selectables = useAppSelector((state) => state.selectables);
  const { consultationType, locationType } = useConsultationType();
  const consultation: IRescheduleConsultationData = useAppSelector(
    (state) => state.consultation.rescheduleConsultationData,
  );
  const queryData: IQueryData = useAppSelector((state) => state.common.queryData);
  const queryDataRef = useRef<IQueryData>(queryData);
  queryDataRef.current = queryData;
  const { slots, isLoading }: { slots: ISlotData; isLoading: boolean } = useAppSelector(
    (state) => state.slot.actualSlots,
  );
  const { startDate, endDate } = useGetDays(consultationType, locationType, consultation?.dueDate);
  const { scheduleAppointment: updateAppointment, isUpdatingAppointment } = useScheduleAppointment();
  const { holdSlot, isLoading: isHoldingSlot } = useHoldSlot();
  const orderConfirmed: boolean = useAppSelector((state) => state.order?.orderConfirmed);
  const { ctaEventCapture } = usePosthog();
  const { generatePreview } = useInvoice();

  const next = useCallback(
    async (payload: {
      switchToInstant?: boolean;
      selectables: Selectables;
      trackSlots: (eventName: string, selectedSlot: ISlot, flow: string) => void;
    }) => {
      if (payload?.switchToInstant) {
        ctaEventCapture(CtaEvents.INSTANT);
        dispatch(updateLocationCode(INSTANT_LOCATION_CODE));
        router.push({ route: Routes.APPOINTMENT, subRoute: Direction.CHECKOUT });
        return;
      }
      ctaEventCapture(CtaEvents.SLOT);
      const { selectables } = payload ?? {};
      holdSlot(selectables.slot)
        .then((res) => {
          if (res) router.push({ route: Routes.APPOINTMENT, subRoute: Direction.CHECKOUT });
        })
        .catch(() => {
          showNotification(NotificationType.ERROR, 'Slot is not available', 3000);
          listSlots();
        });
    },
    [dispatch],
  );

  const listSlots = useCallback(() => {
    dispatch(
      SlotActions.GetSlots({
        startDate: dayjs(startDate).toISOString(),
        endDate: dayjs(endDate).endOf('day').toISOString(),
        consultationTypeId: consultationType.consultationTypeId,
        providerIds: queryData?.providerId,
        excludedProviderIds: queryData?.excludedProviderId,
        preferredLanguages: locationType?.id === ONLINE_LOCATION_ID ? queryData?.selectedLanguages : [],
        locationId: locationType?.id,
        speciality: queryData?.speciality,
        flow: PAGE.APPOINTMENT.NAME,
      }),
    ).then(() => {
      initializeSlotEvents();
    });
  }, [initializeSlotEvents]);

  useEffect(() => {
    if (!consultationType?.consultationTypeId || !locationType?.id) return;
    listSlots();
    generatePreview({
      locationId: locationType?.id,
      consultationTypeId: consultationType?.consultationTypeId,
      locationCode: queryData?.locationCode,
    });
    const interval = setInterval(() => {
      listSlots();
    }, 180_000);
    return () => clearInterval(interval);
  }, [dispatch, consultationType, locationType]);

  useEffect(() => {
    if (orderConfirmed) router.push({ route: Routes.APPOINTMENT, subRoute: Direction.CONFIRM_BOOKING });
  }, [orderConfirmed]);

  return {
    next: next,
    disableNext: !selectables?.slot?.startDate && !selectables.isInstant,
    isLoading: isLoading || !!isUpdatingAppointment.current || isHoldingSlot,
  };
};
